import type { PublicPlan } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { captureAttribution } from '../../lib/attribution';
import { meQuery } from '../../lib/session';
import { Button } from '../../ui/button';
import { ErrorBox } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/form';
import { AuthLayout } from './AuthLayout';

function planPrice(p: PublicPlan): string {
  if (p.priceMonth == null && p.priceYear == null) return 'цена по договорённости';
  const parts: string[] = [];
  if (p.priceMonth != null)
    parts.push(`${p.priceMonth.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ${p.currency} в месяц`);
  if (p.priceYear != null)
    parts.push(`${p.priceYear.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ${p.currency} в год`);
  return parts.join(' · ');
}

/** Заявка на доступ к порталу без входа (ГЛ-04): компания, контакт, что интересует */
export function RequestAccessPage() {
  const plans = useQuery({
    queryKey: ['public', 'plans'],
    queryFn: () => api<PublicPlan[]>('/public/plans'),
    staleTime: 300_000,
  });
  // Из демо (ГЛ-03) сюда приходят посмотревшие портал — им вернуться в демо, а не на вход
  const me = useQuery(meQuery);
  const fromDemo = Boolean(me.data?.user.demoRole);
  const [f, setF] = useState({ company: '', name: '', phone: '', email: '', interest: '', website: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sent, setSent] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  const send = useMutation({
    mutationFn: () => api('/public/access-request', { body: { ...f, ...captureAttribution() } }),
    onSuccess: () => setSent(true),
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    send.mutate();
  }

  if (sent) {
    return (
      <AuthLayout
        title="Заявка отправлена"
        subtitle="Мы свяжемся с вами по указанным телефону или почте в ближайший рабочий день."
      >
        <div className="flex flex-col gap-4">
          <p className="text-[14px] text-ink-2">
            После разговора мы создадим компанию в портале и пришлём приглашение администратору — с него
            начинается пробный период на 14 дней без ограничений по разделам выбранного тарифа.
          </p>
          <Link
            to="/login"
            className="inline-flex h-11 w-fit items-center rounded-md border border-line-strong px-4 text-[14px] font-medium text-ink"
          >
            Вернуться ко входу
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Запросить доступ"
      subtitle="Портал для подрядчиков и заказчиков строительства: оставьте контакты — покажем портал и откроем пробный период."
    >
      <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
        <Field label="Компания" htmlFor="ra-company" error={errors.company}>
          <Input
            id="ra-company"
            value={f.company}
            onChange={set('company')}
            autoComplete="organization"
            invalid={!!errors.company}
            required
          />
        </Field>
        <Field label="Как вас зовут" htmlFor="ra-name" error={errors.name}>
          <Input
            id="ra-name"
            value={f.name}
            onChange={set('name')}
            autoComplete="name"
            invalid={!!errors.name}
            required
          />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Телефон" htmlFor="ra-phone" error={errors.phone}>
            <Input
              id="ra-phone"
              type="tel"
              inputMode="tel"
              value={f.phone}
              onChange={set('phone')}
              autoComplete="tel"
              placeholder="+7 7__ ___ __ __"
              invalid={!!errors.phone}
              required
            />
          </Field>
          <Field label="Рабочая почта" htmlFor="ra-email" error={errors.email}>
            <Input
              id="ra-email"
              type="email"
              inputMode="email"
              value={f.email}
              onChange={set('email')}
              autoComplete="email"
              invalid={!!errors.email}
              required
            />
          </Field>
        </div>
        <Field
          label="Что интересует"
          htmlFor="ra-interest"
          hint="Сколько проектов и людей, какие разделы нужны в первую очередь"
          error={errors.interest}
        >
          <Textarea id="ra-interest" value={f.interest} onChange={set('interest')} rows={3} />
        </Field>
        {/* Поле-ловушка для роботов: людям не видно, роботы заполняют — заявка отклоняется */}
        <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden>
          <label htmlFor="ra-website">Сайт</label>
          <input
            id="ra-website"
            tabIndex={-1}
            autoComplete="off"
            value={f.website}
            onChange={set('website')}
          />
        </div>
        <ErrorBox error={Object.keys(errors).length ? null : send.error} />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={send.isPending}
          disabled={!f.company || !f.name || !f.phone || !f.email}
        >
          Отправить заявку
        </Button>
        <p className="text-[13px] text-ink-2">
          Отправляя заявку, вы соглашаетесь, что мы свяжемся с вами по указанным контактам.{' '}
          {fromDemo ? (
            <Link
              to="/"
              className="inline-flex min-h-11 items-center font-medium text-ink underline underline-offset-4"
            >
              Вернуться в демо
            </Link>
          ) : (
            <>
              Уже есть доступ?{' '}
              <Link
                to="/login"
                className="inline-flex min-h-11 items-center font-medium text-ink underline underline-offset-4"
              >
                Войти
              </Link>
            </>
          )}
        </p>
      </form>
      {plans.data?.length ? (
        <section className="mt-10 border-t border-line pt-6" aria-label="Тарифы">
          <h2 className="text-[16px] font-semibold text-ink">Тарифы</h2>
          <p className="mt-1 text-[13px] text-ink-2">
            Подписка на портале помесячно или на год, либо установка на серверах компании по ключу. Цены без
            НДС.
          </p>
          <ul className="mt-4 flex flex-col gap-3">
            {plans.data.map((p) => (
              <li key={p.code} className="rounded-md border border-line px-4 py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="font-medium text-ink">{p.name}</span>
                  <span className="num text-[13px] text-ink-2">{planPrice(p)}</span>
                </div>
                {p.description ? <p className="mt-1 text-[13px] text-ink-2">{p.description}</p> : null}
                <p className="mt-1 text-[13px] text-ink-2">
                  {p.limits.users != null ? `до ${p.limits.users} сотрудников` : 'сотрудники без ограничения'}
                  {p.limits.projects != null
                    ? `, до ${p.limits.projects} проектов`
                    : ', проекты без ограничения'}
                  {p.limits.files_mb != null ? `, файлы до ${Math.round(p.limits.files_mb / 1024)} ГБ` : ''}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </AuthLayout>
  );
}
